import React from 'react';
import { Box, Typography } from '@mui/joy';
import type { ContractField } from '../../../types';
import { CompletedFieldRenderer } from './CompletedFieldRenderer';
import {
  getFieldTypeLabel,
  getSignerColor,
  getSignerLabel,
  hasFieldValue,
} from './contractFieldStyles';

interface PreviewFieldLayerProps {
  /** Fields on this page only, in PDF units with a top-left origin. */
  fields: ContractField[];
  /** Values collected so far, keyed by field id. */
  fieldValues?: Record<string, any>;
  /** The scale the page is rendered at, so PDF units become CSS pixels. */
  scale: number;
}

/** Height of the caption strip, and the clearance a field needs above it. */
const CAPTION_HEIGHT = 16;

/**
 * Everything placed on a contract page, drawn over the rendered PDF.
 *
 * Preview used to open the raw PDF, so a designer who had just spent ten minutes
 * placing signature and date fields saw a document with none of them on it -
 * indistinguishable from having lost the work. This draws both halves: filled
 * values through the same renderer the completed-contract view uses, and a
 * labelled placeholder wherever a field is still waiting on a signer.
 *
 * Placeholders are styled after the field designer - same signer colours, same
 * caption above the box - so a contract reads the same either side of Preview.
 */
export const PreviewFieldLayer: React.FC<PreviewFieldLayerProps> = ({
  fields,
  fieldValues = {},
  scale,
}) => {
  // Split rather than overlay: an unchecked checkbox is a real value to
  // CompletedFieldRenderer but is still waiting on the signer here, and drawing
  // both would stack a placeholder on top of an empty box.
  const filledFields = fields.filter((field) => hasFieldValue(fieldValues[field.id]));
  const emptyFields = fields.filter((field) => !hasFieldValue(fieldValues[field.id]));

  return (
    <Box
      sx={{
        position: 'absolute',
        top: 0,
        left: 0,
        width: '100%',
        height: '100%',
        pointerEvents: 'none',
      }}
    >
      <CompletedFieldRenderer fields={filledFields} fieldValues={fieldValues} scale={scale} />

      {emptyFields.map((field) => {
        const color = getSignerColor(field.signer);
        const top = field.y * scale;
        // A field near the top of the page has no room for a caption above it,
        // so that one hangs below instead of running off the page.
        const captionAbove = top >= CAPTION_HEIGHT;

        return (
          <Box
            key={field.id}
            sx={{
              position: 'absolute',
              left: `${field.x * scale}px`,
              top: `${top}px`,
              width: `${field.width * scale}px`,
              height: `${field.height * scale}px`,
              border: `1.5px dashed ${color}`,
              borderRadius: '2px',
              backgroundColor: `${color}1F`,
            }}
          >
            <Typography
              level="body-xs"
              sx={{
                position: 'absolute',
                ...(captionAbove
                  ? { top: `-${CAPTION_HEIGHT}px`, borderRadius: '2px 2px 0 0' }
                  : { bottom: `-${CAPTION_HEIGHT}px`, borderRadius: '0 0 2px 2px' }),
                left: '-1.5px',
                height: `${CAPTION_HEIGHT}px`,
                lineHeight: `${CAPTION_HEIGHT}px`,
                backgroundColor: color,
                color: '#fff',
                fontSize: '10px',
                fontWeight: 600,
                px: 0.5,
                whiteSpace: 'nowrap',
              }}
            >
              {getFieldTypeLabel(field)}
              {field.required ? ' *' : ''} · {getSignerLabel(field.signer)}
            </Typography>
          </Box>
        );
      })}
    </Box>
  );
};

export default PreviewFieldLayer;
